<script setup lang="ts">
import { RouterView, useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { ElDropdown, ElDropdownMenu, ElDropdownItem, ElAvatar, ElDialog } from 'element-plus'
import Navigation from '@/components/Navigation.vue'
import ChatterView from '@/views/ChatterView.vue'
import WebinarView from '@/views/WebinarView.vue'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const windowWidth = ref(window.innerWidth)

const handleLogout = () => {
  authStore.logout()
}

const avatarSize = computed(() => {
  if (windowWidth.value <= 480) return 24
  if (windowWidth.value <= 768) return 28
  return 32
})

const updateWindowWidth = () => {
  windowWidth.value = window.innerWidth
}

const isChatterModalOpen = computed(() => {
  return !!route.query.chatter
})

const closeChatterModal = () => {
  const query = { ...route.query }
  delete query.chatter
  router.push({ query })
}

const isWebinarModalOpen = computed(() => {
  return !!route.query.webinar
})

const closeWebinarModal = () => {
  const query = { ...route.query }
  delete query.webinar
  router.push({ query })
}

onMounted(() => {
  window.addEventListener('resize', updateWindowWidth)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateWindowWidth)
})
</script>

<template>
  <div class="app">
    <nav class="navbar" v-if="authStore.isAuthenticated">
      <div class="nav-container">
        <div class="nav-brand"></div>

        <!-- Используем новый компонент навигации -->
        <div class="nav-navigation">
          <Navigation />
        </div>

        <div class="nav-user" v-if="authStore.isAuthenticated">
          <el-dropdown @command="handleLogout">
            <div class="user-info">
              <el-avatar :size="avatarSize" class="user-avatar">
                {{ authStore.user?.username?.charAt(0).toUpperCase() }}
              </el-avatar>
              <span class="username">{{ authStore.user?.username }}</span>
              <span class="user-role">({{ authStore.user?.role }})</span>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="logout">
                  <span class="dropdown-icon">🚪</span>
                  Выйти
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </nav>

    <main class="main-content">
      <RouterView />
    </main>

    <el-dialog
      :model-value="isChatterModalOpen"
      :before-close="closeChatterModal"
      class="chatter-modal"
    >
      <ChatterView v-if="isChatterModalOpen" />
    </el-dialog>

    <el-dialog
      :model-value="isWebinarModalOpen"
      :before-close="closeWebinarModal"
      class="chatter-modal"
    >
      <WebinarView v-if="isWebinarModalOpen" />
    </el-dialog>
  </div>
</template>

<style scoped>
.app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.navbar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
  min-width: 0;
  overflow: hidden;
}

.nav-brand {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  min-width: 0;
}

.brand-link {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.5rem;
  font-weight: 700;
  color: #2563eb;
  text-decoration: none;
  transition: all 0.3s ease;
}

.brand-link:hover {
  color: #14b8a6;
  transform: translateY(-1px);
}

.brand-icon {
  font-size: 1.8rem;
}

.nav-navigation {
  flex: 1;
  display: flex;
  justify-content: center;
  min-width: 0;
  overflow: hidden;
}

.nav-user {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  min-width: 0;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.user-info:hover {
  background: rgba(37, 99, 235, 0.1);
}

.username {
  font-weight: 500;
  color: #374151;
}

.user-role {
  font-size: 0.8rem;
  color: #6b7280;
  background: rgba(37, 99, 235, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
}

.dropdown-icon {
  margin-right: 8px;
}

.main-content {
  flex: 1;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

@media (max-width: 768px) {
  .nav-container {
    flex-direction: row;
    height: 50px;
    padding: 8px 16px;
    gap: 8px;
  }

  .brand-link {
    font-size: 1.1rem;
    gap: 4px;
  }

  .user-info {
    padding: 4px 8px;
    gap: 4px;
  }

  .username {
    font-size: 0.85rem;
  }

  .user-role {
    font-size: 0.7rem;
    padding: 1px 4px;
  }
}

@media (max-width: 480px) {
  .nav-container {
    height: 45px;
    padding: 6px 8px;
    gap: 4px;
  }

  .brand-link {
    font-size: 1rem;
    gap: 2px;
  }

  .user-info {
    padding: 2px 4px;
    gap: 2px;
  }

  .username {
    font-size: 0.75rem;
    max-width: 60px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .user-role {
    display: none;
  }
}

@media (max-width: 360px) {
  .nav-container {
    padding: 4px 6px;
    gap: 2px;
  }

  .brand-link {
    gap: 0;
  }

  .username {
    display: none;
  }

  .user-info {
    padding: 2px;
  }

  .nav-navigation {
    flex: 0.8;
  }
}

.logo {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  margin-right: 10px;
}

@media (max-width: 768px) {
  .logo {
    width: 32px;
    height: 32px;
    margin-right: 6px;
  }
}

@media (max-width: 480px) {
  .logo {
    width: 28px;
    height: 28px;
    margin-right: 4px;
  }
}
</style>

<style>
.chatter-modal.el-dialog {
  --el-dialog-width: 80%;
  padding: 2em 4em;
}
@media (max-width: 900px) {
  .chatter-modal.el-dialog {
    --el-dialog-width: 100%;
    padding: 1em;
  }
}
</style>
